import * as React from 'react';
import { DataGrid, DataGridCtor } from './DataGrid';
import { DataGridColumn } from './DataGrid.Column';
import { Row, Column } from './Grid';
import { Res } from '../../api';
import { Dialog, DialogActions, showMessage } from './Dialog';
import { whenEscKey, whenEnterKey, debounce } from './Utils';
import Button from './Button';


interface RemoteMultiSelectGridProps extends React.HTMLAttributes<RemoteMultiSelectGridProps> {
	source: (filter: string) => Promise<any[]>;
	height?: number;
	itemKey: (item: any) => any;
	columns: DataGridColumn<any>[];
}

class RemoteMultiSelectGrid extends React.Component<RemoteMultiSelectGridProps, { options: any[] }> {

	private dataGrid = React.createRef<DataGrid<any>>();
	private inputSearch = React.createRef<HTMLInputElement>();
	private mounted = false;

	constructor(props: RemoteMultiSelectGridProps) {
		super(props);

		this.state = {
			options: props.source instanceof Array ? props.source || [] : []
		};
	}

	componentDidMount() {
		this.mounted = true;
	}

	componentWillUnmount() {
		this.mounted = false;
	}

	getSelectedItems() {
		return this.dataGrid.current!.getSelectedItems();
	}

	private ensureDataGridSelection() {
		if (this.dataGrid.current!.getSelectedItems().length === 0)
			this.dataGrid.current!.selectFirstItem();
	}

	render() {

		const Grid: DataGridCtor<any> = DataGrid;

		const searchLocal = debounce(() => {
			// Por el debounce podría dispararse esto cuando ya no está montado el componente
			if (this.mounted) {
				this.dataGrid.current!.filterBy(this.inputSearch.current!.value);
				this.ensureDataGridSelection();
			}
		});

		const searchRemote = async () => {
			if (this.props.source instanceof Array)
				throw Error('Esta función no se debería llamar si el origen de datos es local');

			const options = await this.props.source(this.inputSearch.current!.value);
			// Podrían desmontar el componente mientras se ejecuta la búsqueda
			if (this.mounted)
				this.setState({ options }, () => this.ensureDataGridSelection());
		};

		const onInputKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
			if (this.dataGrid.current?.handleExternalKeyDown(e))
				return;

			// Si es búsqueda local, la lanzo (ya está debounceada)
			if (this.props.source instanceof Array) {
				searchLocal();
				return;
			}

			// Estoy en una búsqueda contra el servidor. Si no tengo nada en el grid, lanzo
			// la búsqueda. Si tengo algo, dejo que el diálogo que sacó este componente se
			// cierre con el item seleccionado
			if (e.key === 'Enter' && this.dataGrid.current!.getSelectedItems().length === 0) {
				e.preventDefault();
				e.stopPropagation();
				await searchRemote();
			}
			else {
				this.setState({ options: [] });
			}
		};

		const searchControl = this.props.source instanceof Array
			? <Row><Column width='full'>
				<div className='form-group'>
					<input ref={this.inputSearch} type='text' className='form-control' placeholder={Res.WA.WA_SEARCH_PLACE_HOLDER()} onKeyDown={onInputKeyDown} />
				</div>
			</Column>
			</Row>
			: <Row>
				<div className='col-sm-10 pull-left'>
					<input ref={this.inputSearch} type='text' className='form-control' placeholder={Res.WA.WA_SEARCH_PLACE_HOLDER()} onKeyDown={onInputKeyDown} />
				</div>
				<div className='col-sm-2'>
					<Button buttonStyle='info' className='pull-right' onClick={searchRemote}><i className='fa fa-search'></i> {Res.Common.SEARCH_BTN()}</Button>
				</div>
			</Row>;



		return <div>
			{searchControl}

			<Row className='m-t' >
				<Column width='full'>
					<Grid
						height={this.props.height || 300}
						itemKey={this.props.itemKey}
						ref={this.dataGrid}
						id={this.props.id || 'datagrid_items'}
						columns={this.props.columns}
						initialData={this.state.options}
						shouldPersistState={false}
						multiSelect={true}
						hideExportButton={true}
						maxHeight={400} />
				</Column>
			</Row>
		</div >;
	}
}

interface RemoteMultiSelectDialogProps extends React.HTMLAttributes<RemoteMultiSelectDialog> {
	grid: RemoteMultiSelectGridProps;
	title: string;
	helpText: string;
	acceptOnEnterKey: boolean;
	actions?: DialogActions<any[]>;
}

export class RemoteMultiSelectDialog extends React.Component<RemoteMultiSelectDialogProps, EmptyState> {

	private itemGrid = React.createRef<RemoteMultiSelectGrid>();

	constructor(props: RemoteMultiSelectDialogProps) {
		super(props);
		this.onOK = this.onOK.bind(this);
		this.onCancel = this.onCancel.bind(this);
	}

	private async onOK() {
		if (!this.props.actions)
			return;

		const selectedItems = this.itemGrid.current!.getSelectedItems();

		if (selectedItems.length === 0) {
			await showMessage(Res.Msg.MSG_MUST_SELECT_A_VALUE());
			return;
		}

		this.props.actions.closeWith(selectedItems);
	}

	private onCancel() {
		this.props.actions?.cancel();
	}

	render() {

		const onDefaultKeyDown = (e: React.KeyboardEvent<any>) => {
			whenEnterKey(this.onOK)(e);
			whenEscKey(this.onCancel)(e);
		};

		return <Dialog onKeyDown={onDefaultKeyDown}>
			<div className='modal-content'>
				<div className='modal-header'>
					<h4 className='modal-title'>{this.props.title}</h4>
				</div>
				<div className='modal-body'>
					<p>{this.props.helpText}</p>
					<RemoteMultiSelectGrid ref={this.itemGrid} {... this.props.grid} />
				</div>

				<div className='modal-footer'>
					<Button buttonStyle='info' onClick={this.onOK}>{Res.Common.OK_BTN()}</Button>
					<Button onClick={this.onCancel}>{Res.Common.CANCEL_BTN()}</Button>
				</div>
			</div>
		</Dialog>;
	}
}